import { useLocation, useParams, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import LoadingPage from "../../public/LoadingPage";
import UnknownProcessTypeView from "../error/UnknownProcessTypeView";
import { useGetProcessUIData } from "../../../hooks/entity";
import { useLmcUserView } from "../../../contexts/AuthContext";
import LoyaltyCardRenderer from "../../atoms/LoyaltyCardRenderer";
import { useLanguageDetection } from "../../../hooks/useLanguageDetection";
import { generateLoyaltyProgramHelmet } from "../../../utils/helmetUtils";
import { ProcessData } from "../../../types/BusinessCardTypes";
import { GameOverlay } from "../../games";

interface LocationState {
  from?: string;
  processData?: ProcessData;
  cacheTimestamp?: number;
}
const CACHE_TTL = 2 * 60 * 1000;

const LoyaltyProgramApp = () => {
  const { processId } = useParams<{ processId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { dispatch } = useLmcUserView();

  const [showGameOverlay, setShowGameOverlay] = useState(true);

  const isMainLoyaltyPage =
    location.pathname === `/loyalty-program/${processId}` ||
    location.pathname === `/loyalty-program/${processId}/`;

  const navigationState = location.state as LocationState;
  const hasNavigationData = Boolean(navigationState?.processData);

  const isCacheValid = useMemo(() => {
    if (!hasNavigationData || !navigationState?.cacheTimestamp) {
      return false;
    }
    const now = Date.now();
    const cacheAge = now - navigationState.cacheTimestamp;
    return cacheAge < CACHE_TTL;
  }, [hasNavigationData, navigationState?.cacheTimestamp]);

  const cachedProcessData = isCacheValid
    ? navigationState?.processData || null
    : null;

  const {
    data: processData,
    isLoading,
    isError,
  } = useGetProcessUIData(processId as string, {
    enabled: !isCacheValid && !!processId,
    onSuccess: (data) => {
      dispatch({
        type: "ENTITY",
        payload: data,
      });
    },
  });

  const finalProcessData = cachedProcessData || processData;

  const entityIdentification =
    finalProcessData?.entity_data?.entity_identification || "";

  const handleGameClose = () => {
    setShowGameOverlay(false);
    dispatch({ type: "SKIP_GAMING", payload: {} });
  };

  const handleLoginRequired = () => {
    navigate(`/loyalty-program/${processId}/login`, {
      state: { from: location.pathname },
    });
  };

  useEffect(() => {
    const finalData = cachedProcessData || processData;
    if (!finalData) return;

    dispatch({ type: "ENTITY", payload: finalData });
  }, [cachedProcessData, processData, dispatch]);

  useLanguageDetection(finalProcessData);

  if (isLoading && !isCacheValid) {
    return <LoadingPage />;
  }

  if (isError && !isCacheValid) {
    return <UnknownProcessTypeView processType={undefined} />;
  }

  if (!finalProcessData) {
    return <UnknownProcessTypeView processType={undefined} />;
  }

  return (
    <>
      {generateLoyaltyProgramHelmet({
        processData: finalProcessData,
        processId,
      })}

      {showGameOverlay &&
        finalProcessData?.process_data?.gaming?.enabled &&
        isMainLoyaltyPage && (
          <GameOverlay
            entityIdentification={entityIdentification}
            gamingConfig={finalProcessData?.process_data?.gaming}
            onClose={handleGameClose}
            onLoginRequired={handleLoginRequired}
          />
        )}

      <LoyaltyCardRenderer
        customerIdentification={
          finalProcessData?.entity_data?.owner_data?.user_identification || ""
        }
        entityIdentification={
          finalProcessData?.entity_data?.entity_identification || ""
        }
        entity={{
          name: finalProcessData?.entity_data?.entity_name || "Business",
          description: finalProcessData?.entity_data?.entity_description,
          logo: finalProcessData?.entity_data?.entity_logo,
          accentColor: finalProcessData?.entity_data?.accent_color,
        }}
        program={{
          type: finalProcessData?.process_data?.lmc_type || "POINT_CARD",
          title: finalProcessData?.process_data?.title,
          comment: finalProcessData?.process_data?.comment,
          color: finalProcessData?.process_data?.color,
          bonusText: finalProcessData?.process_data?.bonus_text,
          expiryText: finalProcessData?.process_data?.expiry_text,
          redirectionLinks: finalProcessData?.process_data?.redirectionLinks,
          badges: finalProcessData?.process_data?.badges,
        }}
        member={{
          id:
            finalProcessData?.entity_data?.owner_data?.user_identification ||
            "",
          fullName:
            `${finalProcessData?.entity_data?.owner_data?.first_name || ""} ${
              finalProcessData?.entity_data?.owner_data?.last_name || ""
            }`.trim() || "Member",
          avatar: finalProcessData?.entity_data?.owner_data?.avatar,
        }}
        qr={{
          data: `${process.env.REACT_APP_HOST_URL}/loyalty-program/${processId}`,
          isValid: true,
        }}
        shareUrl={`${process.env.REACT_APP_HOST_URL}/loyalty-program/${processId}`}
        rewardPath={`/loyalty-program/${processId}/rewards`}
        googleReview={{
          enabled:
            finalProcessData?.process_data?.google_places?.review_link_enabled,
          place_id:
            finalProcessData?.process_data?.google_places?.google_place_id,
        }}
        widget={{
          enabled: finalProcessData?.process_data?.widget?.enabled || false,
          bot_id: finalProcessData?.process_data?.widget?.bot_id,
          mode: "inline",
        }}
        hideAuth={
          showGameOverlay &&
          finalProcessData?.process_data?.gaming?.enabled &&
          isMainLoyaltyPage
        }
      />
    </>
  );
};

export default LoyaltyProgramApp;
